'use client';

import Image from 'next/image';
import Giscus from '@giscus/react';
import { useParams } from 'next/navigation';
import { useEffect, type JSX } from 'react';
import { formatDate } from '@core/utils/dateUtils';
import { Box, Flex, Show, Stack } from '@react/chakra';
import { useSuspenseQuery } from '@react/utils/queryHooks';
import { Markdown, NextIcon, PreviousIcon } from '@react/chakra/core';

import { BackButton, Content, DonationButton } from '~/components';

import { postOptions } from '../queries';
import { nextChapterInfo, postTitle, previousChapterInfo } from '../utils';
import {
  ChapterButton,
  TableOfContents,
  SeriesTableOfContents,
} from './components';

interface IParamsProps {
  slug: string;
  [key: string]: string;
}

export function PostDetail(): JSX.Element {
  const { slug } = useParams<IParamsProps>();

  const { data } = useSuspenseQuery(
    postOptions({
      slug,
    }),
  );

  const nextChapter = nextChapterInfo(data);
  const previousChapter = previousChapterInfo(data);
  const {
    image,
    content,
    thumbnail,
    seriesPost,
    publishedAt,
    readingTime,
    githubDiscussionId,
  } = data;

  useEffect(() => {
    const headingList = document.querySelectorAll(
      `
        .markdown-content h1, 
        .markdown-content h2, 
        .markdown-content h3, 
        .markdown-content h4
      `,
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const tocHeadingList = document.querySelectorAll(
              `
                .toc h1, 
                .toc h2, 
                .toc h3, 
                .toc h4
              `,
            );

            tocHeadingList.forEach((heading) => {
              heading.removeAttribute('data-status');
            });

            document
              .querySelector(`.toc [id="${entry.target.id}"]`)
              ?.setAttribute('data-status', 'active');
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0% 0% -35% 0%',
      },
    );

    headingList.forEach((node) => {
      observer.observe(node);
    });

    return () => {
      headingList.forEach((node) => {
        observer.unobserve(node);
      });
    };
  }, []);

  return (
    <Stack gap={20} maxW='100%'>
      <BackButton text='Back to posts' href='/posts' />
      <Stack gap={16}>
        <Content
          title={postTitle(data)}
          content={`${formatDate(publishedAt)} — ${readingTime} min read`}
        />
        <Show when={!!(image || thumbnail)}>
          <Box
            asChild
            mx={-5}
            w='100%'
            maxW='100%'
            objectFit='cover'
            borderRadius='8px'
            alignSelf='center'
            objectPosition='center'
          >
            <Image
              width={300}
              height={200}
              alt='Post Image'
              src={image || thumbnail || ''}
            />
          </Box>
        </Show>
      </Stack>
      <Box pos='relative'>
        <Show when={!!seriesPost}>
          <SeriesTableOfContents data={data} />
        </Show>
        <Box className='markdown-content'>
          <Markdown content={content} />
        </Box>
        <Flex
          justifyContent='space-between'
          mt={!!previousChapter || !!nextChapter ? 20 : 0}
          flexDir={{
            base: 'column',
            md: 'row',
          }}
        >
          <Box>
            <Show when={!!previousChapter}>
              <ChapterButton data={previousChapter} icon={<PreviousIcon />} />
            </Show>
          </Box>
          <Box>
            <Show when={!!nextChapter}>
              <ChapterButton
                type='next'
                data={nextChapter}
                icon={<NextIcon />}
              />
            </Show>
          </Box>
        </Flex>
        <TableOfContents content={content} />
      </Box>
      <DonationButton />
      <Box>
        <Giscus
          lang='en'
          theme='dark'
          loading='lazy'
          category='Q&A'
          emitMetadata='0'
          mapping='specific'
          inputPosition='top'
          reactionsEnabled='1'
          repoId='R_kgDOJn-tPw'
          term={githubDiscussionId}
          categoryId='DIC_kwDOJn-tP84CWxFR'
          repo='zgid123/personal-blog-discussion'
        />
      </Box>
    </Stack>
  );
}
